import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faPen, faPlus, faFilter, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import ColumnVisibilityModal from '../../components/ColumnVisibilityModal';
import { Link } from 'react-router-dom';
import Table from '../../components/Table';
import Pagination from '../../components/Pagination';
import { fetchFuncionarios } from '../../services/funcionarioService';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import { useAuth } from '../../contexts/AuthContext';
import { useDebounce } from '../../hooks/useDebounce';

const columns = [
  { Header: 'ID', accessor: 'idPessoa' },
  { Header: 'Status', accessor: 'status' },
  { Header: 'Nome', accessor: 'nome' },
  { Header: 'CPF', accessor: 'cpf' },
  { Header: 'Email', accessor: 'email' },
  { Header: 'Data de Nascimento', accessor: 'dataNascimento', Cell: value => formatDate(value) },
  { Header: 'Telefone Celular', accessor: 'telefoneCelular' },
  { Header: 'Telefone Residencial', accessor: 'telefoneResidencial' },
  { Header: 'Telefone Comercial', accessor: 'telefoneComercial' },
  { Header: 'Escolaridade', accessor: 'escolaridade' },
  { Header: 'Estado Civil', accessor: 'estadoCivil' },
  { Header: 'Identidade', accessor: 'identidade' },
  { Header: 'Órgão Expedidor', accessor: 'orgaoExpedidor' },
  { Header: 'Bairro', accessor: 'bairro' },
  { Header: 'Cidade', accessor: 'cidade' },
  { Header: 'Estado', accessor: 'estado' },
  { Header: 'CEP', accessor: 'cep' },
  { Header: 'Logradouro', accessor: 'logradouro' },
  { Header: 'Número', accessor: 'numero' },
  { Header: 'Complemento', accessor: 'complemento' },
  { Header: 'País', accessor: 'pais' },
  { Header: 'Cargo', accessor: 'cargo' },
  { Header: 'Estrangeiro', accessor: 'estrangeira', Cell: v => v ? 'Sim' : 'Não' },
  { Header: 'Cadastrado Por', accessor: 'cadastradoPor' },
  { Header: 'Alterado Por', accessor: 'alteradoPor' },
  { Header: 'Alterado Em', accessor: 'alteradoEm', Cell: value => formatDateTime(value) },
];

const pageSizes = [5, 10, 20, 50, 100, 200, 500, 1000];

const FuncionariosList = () => {
  const { user } = useAuth();
  
  const [filters, setFilters] = useState({
    nome: '',
    cpf: '',
    email: '',
    bairro: '',
    cidade: '',
    estado: '',
    escolaridade: '',
    estadoCivil: '',
    cargo: '',
    status: '',
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filtrar colunas baseado no nível de acesso
  const colunasAuditoria = ['cadastradoPor', 'alteradoPor', 'alteradoEm'];
  
  const nivelAcesso = user?.nivelAcesso || user?.perfil?.nivelAcesso || user?.nivel_acesso || 0;
  
  const colunasDisponiveis = nivelAcesso >= 5 
    ? columns 
    : columns.filter(col => !colunasAuditoria.includes(col.accessor));
  
  const [visibleColumns, setVisibleColumns] = useState(colunasDisponiveis.map(col => col.accessor));
  const [funcionarios, setFuncionarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 5,
    total: 0,
    totalPages: 0
  });

  // Só consulta a API depois que o usuário para de digitar no filtro,
  // em vez de uma requisição por tecla.
  const filtrosComAtraso = useDebounce(filters, 400);

  // Função para buscar funcionários
  const loadFuncionarios = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await fetchFuncionarios({
        page,
        limit: pageSize,
        filters: filtrosComAtraso
      });
      
      setFuncionarios(result.data || []);
      setPagination(result.pagination || {});
    } catch (err) {
      console.error('Erro ao carregar funcionários:', err);
      setError('Erro ao carregar funcionários');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFuncionarios();
  }, [page, pageSize, filtrosComAtraso]);

  // Modal handlers
  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);
  const handleToggleColumn = (accessor) => {
    setVisibleColumns(prev =>
      prev.includes(accessor)
        ? prev.filter(col => col !== accessor)
        : [...prev, accessor]
    );
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
    setPage(1); // Reset para primeira página ao filtrar
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(1); // Reset para primeira página ao mudar tamanho
  };

  return (
    <div className="flex-1 w-full max-w-full overflow-x-hidden">
      <div className="px-2 sm:px-6">
        <div className="max-w-full">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
            <h2 className="text-lg sm:text-2xl font-bold text-gray-800">Listagem de Funcionários</h2>
            <Link
              to="/funcionarios/create"
              className="w-full sm:w-auto px-3 py-2 sm:px-4 sm:py-2 rounded bg-green-600 text-white text-sm sm:text-base font-semibold shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 flex items-center justify-center gap-2"
            >
              <FontAwesomeIcon icon={faPlus} />
              <span>Novo Funcionário</span>
            </Link>
          </div>
          <ColumnVisibilityModal
            columns={colunasDisponiveis}
            visibleColumns={visibleColumns}
            onChange={handleToggleColumn}
            onClose={handleCloseModal}
            isOpen={isModalOpen}
          />

          {/* Botão para mostrar/ocultar filtros (mobile) */}
          <div className="mb-4">
            <button
              type="button"
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 rounded bg-blue-600 text-white text-sm font-medium shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
              onClick={() => setShowFilters(!showFilters)}
            >
              <FontAwesomeIcon icon={faFilter} />
              <span>{showFilters ? 'Ocultar Filtros' : 'Exibir Filtros'}</span>
              <FontAwesomeIcon icon={showFilters ? faChevronUp : faChevronDown} className="text-xs" />
            </button>
          </div>

          {/* Filtros colapsáveis */}
          {showFilters && (
            <div className="max-w-full mb-6">
              <div className="flex flex-col sm:flex-row gap-2 flex-wrap">
                <input
                  type="text"
                  name="nome"
                  value={filters.nome}
                  onChange={handleFilterChange}
                  placeholder="Filtrar por nome"
                  className="w-full sm:w-auto px-2 py-1.5 text-sm rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400"
                />
                <input
                  type="text"
                  name="cpf"
                  value={filters.cpf}
                  onChange={handleFilterChange}
                  placeholder="Filtrar por CPF"
                  className="w-full sm:w-auto px-2 py-1.5 text-sm rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400"
                />
                <input
                  type="text"
                  name="email"
                  value={filters.email}
                  onChange={handleFilterChange}
                  placeholder="Filtrar por Email"
                  className="w-full sm:w-auto px-2 py-1.5 text-sm rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400"
                />
                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="w-full sm:w-auto px-2 py-1.5 text-sm rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400"
                >
                  <option value="">Todos os Status</option>
                  <option value="Ativo">Ativo</option>
                  <option value="Inativo">Inativo</option>
                </select>
              </div>
            </div>
          )}
          
          {loading && (
            <div className="text-center py-4">
              <span>Carregando funcionários...</span>
            </div>
          )}
          
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2 max-w-full">
            <div className="order-2 sm:order-1 w-full sm:w-auto">
              <Pagination 
                currentPage={pagination.page || page} 
                totalPages={pagination.totalPages || 1}
                totalItems={pagination.total || 0}
                itemsPerPage={pageSize}
                onPageChange={handlePageChange} 
              />
            </div>
            <div className="order-1 sm:order-2 flex flex-col sm:flex-row items-start sm:items-center w-full sm:w-auto gap-2">
              <span className="text-sm text-gray-700 whitespace-nowrap">
                {loading ? 'Carregando...' : `Total: ${pagination.total || 0} funcionários`}
              </span>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <label htmlFor="pageSize" className="text-sm text-gray-700 whitespace-nowrap">Por página:</label>
                <select
                  id="pageSize"
                  value={pageSize}
                  onChange={e => handlePageSizeChange(Number(e.target.value))}
                  className="px-2 py-1.5 text-sm rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400"
                >
                  {pageSizes.map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-3 py-1.5 text-sm rounded bg-green-600 text-white font-medium shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400"
                onClick={handleOpenModal}
                aria-label="Exibir/Ocultar colunas"
              >
                <FontAwesomeIcon icon={faEye} />
                <span>Colunas</span>
              </button>
            </div>
          </div>
          
          {/* Tabela com scroll horizontal em mobile */}
          {!loading && !error && (
            <div className="max-w-full overflow-x-auto">
              <Table
                columns={colunasDisponiveis.filter(col => visibleColumns.includes(col.accessor)).map(col => {
                  if (col.accessor === 'nome') {
                    return {
                      ...col,
                      Cell: (value, row) => (
                        <div className="flex items-center gap-2">
                          <span>{value}</span>
                          <Link
                            to={`/funcionarios/${row.idPessoa}/edit`}
                            className="text-green-600 hover:text-green-800 p-1 rounded focus:outline-none"
                            title="Editar"
                          >
                            <FontAwesomeIcon icon={faPen} />
                          </Link>
                        </div>
                      )
                    };
                  }
                  return col;
                })}
                data={funcionarios}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FuncionariosList;
